import { createRouter, createWebHistory } from 'vue-router'
import { ElMessage } from 'element-plus'
import authDialog from '@/utils/authDialog'
import authState from '@/utils/authState'

// 修改Admin组件的导入方式
// 确保引入路径正确 - 移除动态字符串路径
// const adminImportPath = '../views/Admin.vue'
// 检查文件是否存在的日志信息
// console.log('管理员组件导入路径:', adminImportPath)

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: () => import('../views/Home.vue'),
      meta: { requiresAuth: false }
    },
    // 测试页面路由 - 用于测试日志工具
    {
      path: '/test-logger',
      name: 'TestLogger',
      component: () => import('../components/LoggerTest.vue'),
      meta: { requiresAuth: false }
    },
    // 管理员界面路由 - 使用嵌套路由
    {
      path: '/admin',
      name: 'Admin',
      // 修复: 不使用动态字符串路径，直接使用静态导入路径
      component: () => import('../views/Admin.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
      children: [
        {
          path: 'dashboard',
          name: 'AdminDashboard',
          component: () => import('../views/admin/Dashboard.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'homepage',
          name: 'AdminHomepage',
          component: () => import('../views/admin/Homepage.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'users',
          name: 'AdminUsers',
          component: () => import('../views/admin/UserManagement.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'materials',
          name: 'AdminMaterials',
          component: () => import('../views/admin/Materials.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'tools',
          name: 'AdminTools',
          component: () => import('../views/admin/ToolManagement.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'monetize',
          name: 'AdminMonetize',
          component: () => import('../views/admin/MonetizeManagement.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'custom',
          name: 'AdminCustom',
          component: () => import('../views/admin/CustomManagement.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'tutorials',
          name: 'AdminTutorials',
          component: () => import('../views/admin/TutorialManagement.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: 'tool-introductions',
          name: 'AdminToolIntroductions',
          component: () => import('../views/admin/ToolIntroductionManagement.vue'),
          meta: { requiresAuth: true, requiresAdmin: true }
        },
        {
          path: '',
          redirect: '/admin/dashboard'
        }
      ]
    },
    // 材料相关路由
    {
      path: '/materials',
      name: 'Materials',
      redirect: '/materials/character',
      meta: { requiresAuth: false },
      children: [
        {
          path: 'character',
          name: 'Character',
          component: () => import('../views/materials/Character.vue')
        },
        {
          path: 'scene',
          name: 'Scene',
          component: () => import('../views/materials/Scene.vue')
        },
        {
          path: 'expression',
          name: 'Expression',
          component: () => import('../views/materials/Expression.vue')
        },
        {
          path: 'animal',
          name: 'Animal',
          component: () => import('../views/materials/Animal.vue')
        },
        {
          path: 'prop',
          name: 'Prop',
          component: () => import('../views/materials/Prop.vue')
        },
        {
          path: 'effect',
          name: 'Effect',
          component: () => import('../views/materials/Effect.vue')
        },
        {
          path: 'ai',
          name: 'AI',
          component: () => import('../views/materials/AI.vue')
        },
        {
          path: 'recent',
          name: 'RecentUpdates',
          component: () => import('../views/materials/RecentUpdates.vue')
        }
      ]
    },
    {
      path: '/custom',
      name: 'Custom',
      component: () => import('../views/Custom.vue'),
      meta: { requiresAuth: true, exact: true }
    },
    {
      path: '/tools',
      name: 'Tools',
      component: () => import('../views/Tools.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/monetize',
      name: 'Monetize',
      component: () => import('../views/Monetize.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tutorial',
      name: 'Tutorial',
      component: () => import('../views/Tutorial.vue'),
      meta: { requiresAuth: false }
    },
    // 添加AI工具页面路由
    {
      path: '/ai-tool',
      name: 'AITool',
      component: () => import('../views/AITool.vue'), // 确保AITool.vue文件存在且没有语法错误
      meta: { requiresAuth: false }
    },
    // 捕获所有未匹配的路由，重定向到首页
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ]
})

// 路由守卫逻辑
router.beforeEach((to, from, next) => {
  // 检查本地存储中的用户状态
  const token = localStorage.getItem('token')
  const userStr = localStorage.getItem('user')
  const isTokenValid = !!token && token.length > 20 // 简单检查token是否看起来合法
  
  // 使用authState检查登录状态
  const isLoggedIn = authState.isLoggedIn.value && isTokenValid
  const isAdmin = isLoggedIn && authState.user.value?.role === 'admin'
  
  // 如果localStorage中有token但不合法，清除登录状态
  if (token && !isTokenValid) {
    console.warn('路由守卫: 检测到无效token，执行登出')
    authState.logout()
  }
  
  console.log('Route guard check:', { 
    from: from.path, 
    to: to.path, 
    requiresAuth: to.meta.requiresAuth,
    requiresAdmin: to.meta.requiresAdmin,
    isLoggedIn,
    isAdmin,
    tokenValid: isTokenValid
  })

  // 添加额外的调试信息
  if (to.path.startsWith('/admin')) {
    console.log('ADMIN PATH DETECTED, Auth status:', {
      isLoggedIn,
      isAdmin,
      pathRequiresAdmin: to.meta.requiresAdmin
    })
  }

  // 处理直接访问"/login"路径的情况
  if (to.path === '/login') {
    if (isLoggedIn) {
      // 如果用户是管理员，导向管理后台；否则导向首页
      if (isAdmin) {
        next('/admin')
      } else {
        next('/')
      }
    } else {
      // 显示登录弹窗并导航到首页
      authDialog.show()
      next('/')
    }
    return
  }

  // 检查管理员权限
  if (to.matched.some(record => record.meta.requiresAdmin)) {
    if (!isAdmin) {
      console.log('需要管理员权限，但用户不是管理员')
      // 如果未登录，显示登录弹窗
      if (!isLoggedIn) {
        // 显示登录弹窗，并设置重定向URL
        authDialog.show({ redirectUrl: to.fullPath })
        // 阻止路由导航，保持在当前页面
        next(false)
      } else {
        // 已登录但不是管理员，提示错误
        ElMessage.warning('您没有管理员权限')
        next(false)
      }
      return
    }
  }

  // 检查登录权限
  if (to.meta.requiresAuth && !isLoggedIn) {
    console.log('需要登录权限，但用户未登录，显示登录弹窗')
    // 显示登录弹窗，并设置重定向URL
    authDialog.show({ redirectUrl: to.fullPath })
    // 阻止路由导航，保持在当前页面
    next(false)
    return
  }

  // 检查是否需要精确匹配路径  
  if (to.meta.exact && to.path !== to.matched[0].path) {
    console.log('路径需要精确匹配，但当前路径不匹配')
    next(to.matched[0].path)
    return
  }
  
  // 如果不需要登录或者已经登录，允许访问
  console.log('允许访问:', to.path)
  next()
})

export default router 